import React, { useState } from 'react';
import { Users, Mail, Phone, Award, Star } from 'lucide-react';
import { coaches, coachingGroupsOrder, contactEmails, teams, heroImages } from '../mock';
import { icePatch } from '../mock';
import { Headshot } from '../components/Headshot';

export default function Team() {
  const [filter, setFilter] = useState('All');
  const filters = ['All', 'Football', 'Cheer'];

  const filteredTeams = teams.filter((t) => filter === 'All' || t.division === filter);

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-24 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[2]} alt="team" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/70 to-[#0a1f44]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Meet The Patriots</div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none">OUR MEMBERS</h1>
          <p className="text-white/70 max-w-2xl mt-6 text-lg">
            Every coach, staff member, and scholar makes the Patriots family. Meet the people building champions in Brookline & Jamaica Plain.
          </p>
        </div>
      </section>

      <div className="stripe-sep" />

      {/* Coaches */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Coaching Staff</div>
              <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">THE LEADERS</h2>
            </div>
            <div className="flex items-center gap-2 text-[#0a1f44]/60">
              <Users className="w-4 h-4" />
              <span className="font-heading uppercase tracking-widest text-xs">{coaches.length} Staff Members</span>
            </div>
          </div>
          <div className="space-y-14" data-testid="coaches-grouped">
            {coachingGroupsOrder.map((groupName) => {
              const groupCoaches = coaches.filter((c) => c.division === groupName);
              if (groupCoaches.length === 0) return null;
              return (
                <div key={groupName} data-testid={`coach-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center gap-4 mb-5">
                    <div className="h-0.5 flex-shrink-0 w-10 bg-[#c8102e]" />
                    <div className="font-heading uppercase tracking-[0.3em] text-[#0a1f44] text-sm">{groupName}</div>
                    <div className="h-0.5 flex-1 bg-[#0a1f44]/10" />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {groupCoaches.map((c, i) => (
                      <div key={c.name} data-testid={`coach-card-${c.name.replace(/\s+/g, '-').toLowerCase()}`} className="group relative overflow-hidden bg-[#0a1f44] cursor-pointer" style={{ animationDelay: `${i * 0.05}s` }}>
                        <div className="aspect-[3/4] overflow-hidden relative">
                          <Headshot name={c.name} src={c.img} defaultY={50} className="group-hover:scale-110 transition-transform duration-700" />
                          <img
                            src={icePatch}
                            alt="ICE Patriots patch"
                            data-testid={`coach-patch-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                            className="absolute top-2 right-2 h-14 w-auto object-contain drop-shadow-lg z-10 pointer-events-none"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44] via-[#0a1f44]/40 to-transparent pointer-events-none" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                          <div className="w-10 h-0.5 bg-[#c8102e] mb-3" />
                          <div className="font-display text-xl tracking-wide">{c.name.toUpperCase()}</div>
                          <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e] mt-1">{c.role}</div>
                          {c.phone && (
                            <a
                              href={`tel:${c.phone.replace(/[^0-9+]/g, '')}`}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`coach-phone-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                              className="relative z-10 inline-flex items-center gap-1.5 mt-3 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-1.5 transition-colors"
                            >
                              <Phone className="w-3 h-3" /> {c.phone}
                            </a>
                          )}
                          <p className="text-white/80 text-xs mt-3 leading-relaxed line-clamp-4 group-hover:line-clamp-none transition-all duration-500">{c.bio}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Teams */}
      <section className="py-24 bg-[#f5f6f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Divisions</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">OUR TEAMS</h2>
          </div>
          <div className="flex justify-center gap-2 mb-10">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`team-filter-${f.toLowerCase()}`}
                className={`px-6 py-2.5 font-heading uppercase tracking-widest text-xs transition-colors border-2 ${
                  filter === f ? 'bg-[#c8102e] text-white border-[#c8102e]' : 'bg-white text-[#0a1f44] border-[#0a1f44]/15 hover:border-[#c8102e]'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTeams.map((t) => (
              <div key={t.name} data-testid={`team-card-${t.name.replace(/\s+/g, '-').toLowerCase()}`} className="hover-lift bg-white border-2 border-[#0a1f44]/10 relative overflow-hidden flex flex-col">
                {t.img && (
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#0a1f44]">
                    <img
                      src={t.img}
                      alt={t.name}
                      data-testid={`team-image-${t.name.replace(/\s+/g, '-').toLowerCase()}`}
                      className={`w-full h-full object-cover ${t.name === 'U11 Football' ? 'scale-[1.34] origin-top' : ''}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="relative p-8">
                  <div className="absolute top-0 right-0 bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-1.5">{t.division}</div>
                  <Star className="w-8 h-8 text-[#c8102e] mb-4" />
                  <div className="font-display text-3xl text-[#0a1f44] mb-1 tracking-wide">{t.name.toUpperCase()}</div>
                  <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60 mb-4">{t.age}</div>
                  <p className="text-[#0a1f44]/70 text-sm leading-relaxed">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-24 bg-[#0a1f44] relative overflow-hidden">
        <div className="absolute inset-0 stars-bg" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Award className="w-12 h-12 text-[#c8102e] mx-auto mb-6" />
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Become a Patriot</div>
          <h2 className="font-display text-white text-5xl md:text-6xl mb-6">READY TO JOIN?</h2>
          <p className="text-white/75 text-lg mb-8 max-w-xl mx-auto">
            Contact Program Director Jeff Hammond for registration questions and to start your journey with the Patriots family.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="tel:6178188296" data-testid="team-contact-phone" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-8 py-4 transition-colors">
              <Phone className="w-4 h-4" /> 617-818-8296
            </a>
            {contactEmails.map((email, index) => (
              <a key={email} href={`mailto:${email}`} data-testid={`team-contact-email-${index + 1}`} className="inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white font-heading tracking-wider text-xs sm:text-sm px-5 py-4 transition-colors hover:bg-white hover:text-[#0a1f44] break-all">
                <Mail className="w-4 h-4 shrink-0" /> {email}
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
